import { Trans, useTranslation } from 'next-i18next'

import FaqAccordion, { ItemLink } from './faq-accordion'

const General = () => {
  const { t } = useTranslation('faq')

  const LANDLORD_TIPS = [
    {
      title: t('faq:Why does liv.rent ask for so much personal information?'),
      path: 'why-livrent-asks-for-information',
      answers: [
        {
          steps: [
            <Trans
              key="The information we ask for is exactly what you’ll find on a standard tenancy application. We don’t ask for anything that you wouldn’t share with a landlord or tenant if you wanted to get into a rental agreement. We take your"
              i18nKey="faq:The information we ask for is exactly what you’ll find on a standard tenancy application. We don’t ask for anything that you wouldn’t share with a landlord or tenant if you wanted to get into a rental agreement. We take your"
            >
              The information we ask for is exactly what you’ll find on a
              standard tenancy application. We don’t ask for anything that you
              wouldn’t share with a landlord or tenant if you wanted to get into
              a rental agreement. We take your{' '}
              <ItemLink
                href="https://liv.rent/privacy#personal-information"
                rel="noopener noreferrer"
                target="_blank"
              >
                personal information
              </ItemLink>{' '}
              very seriously.
            </Trans>
          ]
        }
      ]
    },
    {
      title: t('faq:Why is liv.rent asking for my SIN?'),
      path: 'why-livrent-asks-for-SIN',
      answers: [
        {
          steps: [
            <Trans
              key="liv.rent does not require your SIN. If you choose to provide it as part of getting your"
              i18nKey="faq:liv.rent does not require your SIN. If you choose to provide it as part of getting your"
            >
              liv.rent does not require your SIN. If you choose to provide it as
              part of getting your{' '}
              <ItemLink
                href="https://liv.rent/blog/2019/10/trust-score-explained/"
                rel="noopener noreferrer"
                target="_blank"
              >
                Trust Score
              </ItemLink>{' '}
              then it will not be saved by liv.rent. A landlord may ask an
              applicant to provide them with a Trust Score, but it is not
              necessary to use our platform.
            </Trans>
          ]
        },
        {
          steps: [
            <Trans
              key="If you choose to provide your SIN, it will not be saved by liv.rent. Once your credit check is complete and your"
              i18nKey="faq:If you choose to provide your SIN, it will not be saved by liv.rent. Once your credit check is complete and your"
            >
              If you choose to provide your SIN, it will not be saved by
              liv.rent. Once your credit check is complete and your{' '}
              <ItemLink
                href="https://liv.rent/blog/2019/10/trust-score-explained/"
                rel="noopener noreferrer"
                target="_blank"
              >
                Trust Score
              </ItemLink>{' '}
              has been generated,{' '}
              <ItemLink
                href="https://liv.rent/privacy#personal-information"
                rel="noopener noreferrer"
                target="_blank"
              >
                personal information
              </ItemLink>{' '}
              is destroyed immediately. No one, including liv.rent employees,
              can see your data. All information is strictly encrypted with
              AES-256-CTR (similar to military-grade encryption).
            </Trans>
          ]
        },
        {
          steps: [
            t(
              'faq:Trust Score is an intelligent rating system we designed to help renters clearly establish and communicate their credibility as a renter. We use an Equifax Credit Report as part of our analysis. Using a social insurance number (SIN) is a secure way to make sure the right profile is pulled for this report.'
            )
          ]
        },
        {
          steps: [
            t(
              'faq:Once your credit check is complete and your Trust Score has been generated, this personal information is destroyed immediately. No one, including liv.rent employees, can see your data. All information is encrypted with AES-256-CTR encryption.'
            ),
            <br key="breakline" />,
            <Trans
              key="Find out more about how getting a Trust Score streamlines renting for everyone."
              i18nKey="faq:Find out more about how getting a Trust Score streamlines renting for everyone."
            >
              Find out more about how getting a{' '}
              <ItemLink
                href="https://liv.rent/blog/2019/10/trust-score-explained/"
                rel="noopener noreferrer"
                target="_blank"
              >
                Trust Score
              </ItemLink>{' '}
              streamlines renting for everyone.
            </Trans>
          ]
        }
      ]
    },
    {
      title: t('faq:What happens after I upload my ID to liv.rent?'),
      path: 'upload-ID-livrent',
      answers: [
        {
          steps: [
            <Trans
              key="After you have uploaded your government-issued photo ID to liv.rent, we will conduct a strict"
              i18nKey="faq:After you have uploaded your government-issued photo ID to liv.rent, we will conduct a strict"
            >
              After you have uploaded your government-issued photo ID to
              liv.rent, we will conduct a strict{' '}
              <ItemLink
                href="https://liv.rent/blog/2019/10/verification-process/"
                rel="noopener noreferrer"
                target="_blank"
              >
                verification process
              </ItemLink>
              . Your selfie photo will be requested, which is used to match your
              government-issued ID and to confirm your identity. This is to
              protect against identity theft and/or related fraudulent
              occurrences.
            </Trans>
          ]
        },
        {
          steps: [
            t(
              'faq:Upon confirmation of your identity, your government-issued photo ID and all copies of authentication information will be securely destroyed immediately.'
            )
          ]
        }
      ]
    },
    {
      title: t('faq:Who has access to my personal information?'),
      path: 'who-has-access',
      answers: [
        {
          steps: [
            <Trans
              key="No one has access to your personal information unless you have given permission for the customer support team to assist or help you manage your account. We take your"
              i18nKey="faq:No one has access to your personal information unless you have given permission for the customer support team to assist or help you manage your account. We take your"
            >
              No one has access to your personal information unless you have
              given permission for the customer support team to assist or help
              you manage your account. We take your{' '}
              <ItemLink
                href="https://liv.rent/privacy#personal-information"
                rel="noopener noreferrer"
                target="_blank"
              >
                personal information
              </ItemLink>{' '}
              very seriously.
            </Trans>
          ]
        }
      ]
    },
    {
      title: t('faq:Is liv.rent legit?'),
      path: 'is-livrent-legit',
      answers: [
        {
          steps: [
            <Trans
              key="Yes, liv.rent is a Vancouver-based start-up that officially launched in August 2018."
              i18nKey="faq:Yes, liv.rent is a Vancouver-based start-up that officially launched in August 2018."
            >
              Yes, liv.rent is a Vancouver-based start-up that officially
              launched in August 2018. We are a{' '}
              <ItemLink
                href="https://liv.rent/about"
                rel="noopener noreferrer"
                target="_blank"
              >
                rental platform
              </ItemLink>{' '}
              that saves you time and energy by digitalizing the rental process
              for both renters and landlords.
            </Trans>
          ]
        },
        {
          steps: [
            <>
              {t('faq:Renters can', 'Renters can:')}
              <ul>
                <li>
                  <Trans i18nKey="faq:Search for verified rental listings">
                    Search for{' '}
                    <ItemLink href="https://liv.rent/blog/2019/10/verification-process/">
                      verified
                    </ItemLink>{' '}
                    rental listings
                  </Trans>
                </li>
                <li>{t('faq:Store rental documents')}</li>
                <li>
                  <Trans i18nKey="faq:See properties with video tours">
                    See properties with{' '}
                    <ItemLink href="https://liv.rent/blog/2020/05/feature-update-view-rental-listings-online-with-video-tours/'"></ItemLink>{' '}
                    video tours
                  </Trans>
                </li>
                <li>
                  {t('faq:Send digital applications & sign tenancy agreements')}
                </li>
                <li>
                  <Trans i18nKey="faq:And more, see all our free renter features">
                    And more, see all our free{' '}
                    <ItemLink href="https://liv.rent/renters">
                      renter features
                    </ItemLink>
                  </Trans>
                </li>
              </ul>
            </>
          ]
        },
        {
          steps: [
            <>
              {t(
                'faq:Landlords and property managers can',
                'Landlords and property managers can:'
              )}
              <ul>
                <li>{t('faq:Advertise rental listings')}</li>
                <li>
                  {t(
                    `faq:Cross-promote across liv.rent's syndicated channels (Facebook Rental Groups, WeChat, Zumper & more)`
                  )}
                </li>
                <li>
                  <Trans i18nKey="faq:Screen tenants with Trust Score">
                    Screen tenants with
                    <ItemLink href="https://liv.rent/blog/2020/08/trust-score-vs-other-credit-scoring-systems/"></ItemLink>{' '}
                    Trust Score
                  </Trans>
                </li>
                <li>{t('faq:Sign digital lease agreements')}</li>
                <li>{t('faq:Collect rent payments online')}</li>
                <li>
                  <Trans i18nKey="faq:And more, check out all of our landlord features">
                    And more, check out all of our{' '}
                    <ItemLink href="https://liv.rent/landlords">
                      landlord features
                    </ItemLink>
                  </Trans>
                </li>
              </ul>
            </>
          ]
        },
        {
          steps: [
            <Trans
              key="Plus, with liv.rents custom Landlord Dashboard"
              i18nKey="faq:Plus, with liv.rents custom Landlord Dashboard"
            >
              Plus, with liv.rent&apos;s custom{' '}
              <ItemLink
                href="https://liv.rent/blog/2020/10/landlord-dashboard-digital-tools-for-landlords-property-managers/"
                rel="noopener noreferrer"
                target="_blank"
              >
                Landlord Dashboard
              </ItemLink>
              , you can use{' '}
              <ItemLink
                href="https://liv.rent/blog/2020/10/landlord-dashboard-digital-tools-for-landlords-property-managers/#listing-insights"
                rel="noopener noreferrer"
                target="_blank"
              >
                Listing Insights
              </ItemLink>{' '}
              to help price your rental property competitively by comparing
              what&apos;s out there in the market to attract suitable, qualified
              tenants.
            </Trans>
          ]
        },
        {
          steps: [
            t('faq:Visit our office'),
            <br key="breakline" />,
            t('faq:Suite 1A - 1601 Bayshore Dr.'),
            <br key="breakline" />,
            'Vancouver, BC V6G 2V4'
          ]
        },
        {
          steps: [
            t('faq:Reach us at'),
            <br key="breakline" />,
            t('faq:contact by telephone', 'T: (604) 593-3020'),
            <br key="breakline" />,
            t('faq:email by email', 'E: info@liv.rent')
          ]
        },
        {
          steps: [
            t("faq:We're available"),
            <br key="breakline" />,
            t('faq:Monday to Friday 10 a.m. to 6 p.m. PST'),
            <br key="breakline" />,
            t('faq:Saturday 11 a.m. to 4 p.m. PST')
          ]
        }
      ]
    },
    {
      title: t(
        'faq:Why is my phone number required to create an account on liv.rent?'
      ),
      path: 'why-is-phone-number-required',
      answers: [
        {
          steps: [
            t(
              'faq:We ask for your phone number to reduce the number of scam users signing up on our platform and to ensure your safety. Once you have submitted your phone number, we will send you an SMS message for authentication.'
            )
          ]
        }
      ]
    },
    {
      title: t('faq:How does liv.rent protect my personal data?'),
      path: 'how-livrent-protects',
      answers: [
        {
          steps: [
            <Trans
              key="To protect your personal data, all information is encrypted with AES-256-CTR encryption. Plus, all network traffic is secured with the HTTPS secure protocol. We take your"
              i18nKey="faq:To protect your personal data, all information is encrypted with AES-256-CTR encryption. Plus, all network traffic is secured with the HTTPS secure protocol. We take your"
            >
              To protect your personal data, all information is encrypted with
              AES-256-CTR encryption. Plus, all network traffic is secured with
              the HTTPS secure protocol. We take your{' '}
              <ItemLink
                href="https://liv.rent/privacy#security"
                rel="noopener noreferrer"
                target="_blank"
              >
                personal information
              </ItemLink>{' '}
              very seriously.
            </Trans>
          ]
        }
      ]
    },
    {
      title: t('faq:Are the contracts signed on liv.rent legally binding?'),
      path: 'are-livrent-contracts-legally-binding',
      answers: [
        {
          steps: [
            t(
              'faq:Legally binding contracts',
              'Yes, contracts signed on liv.rent are legally binding. When users enter into a tenancy agreement, they are entering into a contract directly with each other. NOTE: liv.rent only serves as a tool provider to facilitate the contract process, but is not a party to any tenancy agreement and has no authority to enforce the terms outlined in the signed agreement.'
            )
          ]
        },
        {
          steps: [
            <Trans
              key="For more information, please refer to liv.rent terms"
              i18nKey="faq:For more information, please refer to liv.rent terms"
            >
              For more information, please refer to liv.rent&apos;s{' '}
              <ItemLink
                href="https://liv.rent/terms"
                rel="noopener noreferrer"
                target="_blank"
              >
                Terms and Conditions
              </ItemLink>
            </Trans>
          ]
        }
      ]
    },
    {
      title: t('faq:Does liv.rent only have listings in Vancouver?'),
      path: 'are-livrent-listings-only-in-Vancouver',
      answers: [
        {
          steps: [
            <Trans
              key="liv.rent currently has listings across all of B.C. and in selected cities including"
              i18nKey="faq:liv.rent currently has listings across all of B.C. and in selected cities including"
            >
              liv.rent currently has listings across all of B.C. and in selected
              cities including{' '}
              <ItemLink
                href="https://liv.rent/rental-listings/city/toronto"
                rel="noopener noreferrer"
                target="_blank"
              >
                Toronto
              </ItemLink>
              ,{' '}
              <ItemLink
                href="https://liv.rent/rental-listings/city/calgary"
                rel="noopener noreferrer"
                target="_blank"
              >
                Calgary
              </ItemLink>
              , and{' '}
              <ItemLink
                href="https://liv.rent/rental-listings/city/edmonton"
                rel="noopener noreferrer"
                target="_blank"
              >
                Edmonton
              </ItemLink>
            </Trans>
          ]
        }
      ]
    }
  ]

  return <FaqAccordion data={LANDLORD_TIPS} />
}

export default General
